import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Compass, Anchor, ArrowRight, Check, Award,
  Calendar, MapPin, Users, Clock, GraduationCap,
} from "lucide-react";
import { useState, useEffect } from "react";

/* ---------- types ---------- */
interface SeminarData {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  location: string;
  max_participants: number;
  current_participants: number;
  instructor_id: string | null;
}

interface InstructorData {
  full_name: string;
  portrait_url: string | null;
  title: string | null;
  bio_short: string | null;
}

/* ---------- countdown hook ---------- */
function useCountdown(targetDate: string) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0 });
  useEffect(() => {
    const calc = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) return { days: 0, hours: 0, mins: 0 };
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        mins: Math.floor((diff / (1000 * 60)) % 60),
      };
    };
    setTimeLeft(calc());
    const t = setInterval(() => setTimeLeft(calc()), 60_000);
    return () => clearInterval(t);
  }, [targetDate]);
  return timeLeft;
}

/* ---------- helper ---------- */
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });

/* ================================================================ */
const DualPathSelector = () => {
  /* seminar data */
  const { data: seminar } = useQuery({
    queryKey: ["next-leadership-seminar"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seminars")
        .select("*")
        .eq("status", "published")
        .gte("start_date", new Date().toISOString())
        .order("start_date", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as SeminarData | null;
    },
  });

  const { data: instructor } = useQuery({
    queryKey: ["seminar-instructor", seminar?.instructor_id],
    enabled: !!seminar?.instructor_id,
    queryFn: async () => {
      const { data } = await supabase
        .from("instructors")
        .select("full_name, portrait_url, title, bio_short")
        .eq("id", seminar!.instructor_id!)
        .single();
      return data as InstructorData | null;
    },
  });

  const countdown = useCountdown(seminar?.start_date || new Date().toISOString());
  const spotsLeft = seminar ? seminar.max_participants - seminar.current_participants : 0;

  return (
    <section className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-6">
        {/* headline */}
          <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-3 text-xs uppercase tracking-wide">
            Bereit für mehr?
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            Wählen Sie Ihren Weg zur KI-Kompetenz
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Zwei Wege, ein Ziel: Souveräne KI-Führung in der Pflege.
          </p>
        </div>

        {/* dual columns */}
        <div className="grid lg:grid-cols-5 gap-8 items-stretch">

          {/* ─── Pfad A: Digital-Akademie (2/5) ─── */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="text-center lg:text-left">
              <Badge className="bg-primary/15 text-primary border-0 mb-2">Pfad A</Badge>
              <h3 className="text-2xl font-bold mb-1">MAIP Digital</h3>
              <p className="text-muted-foreground text-sm">Flexibel, selbstgesteuert & zertifiziert</p>
            </div>

            {/* KI-Pilot */}
            <Card className="border border-border hover:shadow-lg transition-all flex-1">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                    <Compass className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold">KI-Pilot</h4>
                    <p className="text-xs text-muted-foreground">7-Tage-Challenge</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm mb-4">
                  {["16 Pflege-Szenarien", "PROFI-Prompt-Baukasten", "Silber-Zertifikat"].map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/ki-pilot">
                  <Button variant="outline" className="w-full" size="sm">
                    Details <ArrowRight className="ml-1 w-3.5 h-3.5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* KI-Lotse */}
            <Card className="border border-border hover:shadow-lg transition-all flex-1">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-premium/15 flex items-center justify-center">
                    <Anchor className="w-5 h-5 text-premium" />
                  </div>
                  <div>
                    <h4 className="font-bold">KI-Lotse</h4>
                    <p className="text-xs text-muted-foreground">14-Tage-Ausbildung</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm mb-4">
                  {["24 erweiterte Szenarien", "Train-the-Trainer Fokus", "Gold-Zertifikat"].map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-premium flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/lotsen">
                  <Button variant="outline" className="w-full" size="sm">
                    Warteliste <ArrowRight className="ml-1 w-3.5 h-3.5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* ─── Pfad B: Premium-Präsenz (3/5) ─── */}
          <Card className="lg:col-span-3 border-2 border-premium/40 bg-gradient-to-br from-premium-dark via-foreground to-primary/90 text-white relative overflow-hidden shadow-[var(--shadow-premium)]">
            {/* decorative */}
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-premium/10 blur-3xl -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary/15 blur-3xl translate-y-1/3 -translate-x-1/4" />

            <CardContent className="relative z-10 p-8 md:p-10 flex flex-col h-full">
              <div className="flex items-center justify-between mb-6">
                <Badge className="bg-gradient-to-r from-premium to-yellow-400 text-premium-foreground font-bold text-xs uppercase tracking-widest px-3 py-1">
                  🏆 Pfad B – Premium
                </Badge>
                <Badge variant="outline" className="border-premium/50 text-premium text-xs">
                  Intensiv · Vor Ort
                </Badge>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold mb-2">MAIP Executive</h3>
              <p className="text-white/70 mb-6">
                3 Tage, die alles verändern: Vom KI-Versteher zum KI-Projektmanager – mit Strategie, Netzwerk und persönlichem Coaching.
              </p>

              {/* key facts */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { icon: Clock, label: "3 Tage", sub: "Intensiv-Training" },
                  { icon: Users, label: `Max. ${seminar?.max_participants ?? 12}`, sub: "Teilnehmer" },
                  { icon: Award, label: "Zertifikat", sub: "Anerkannt" },
                ].map((f) => (
                  <div key={f.label} className="bg-white/10 rounded-lg p-3 text-center backdrop-blur-sm">
                    <f.icon className="w-5 h-5 mx-auto mb-1 text-premium" />
                    <div className="font-bold text-sm">{f.label}</div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider">{f.sub}</div>
                  </div>
                ))}
              </div>

              {/* instructor */}
              {instructor && (
                <div className="flex items-center gap-4 mb-6 bg-white/5 rounded-lg p-4 border border-white/10">
                  {instructor.portrait_url ? (
                    <img
                      src={instructor.portrait_url}
                      alt={instructor.full_name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-premium/40"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-primary/30 flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-premium" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold">{instructor.full_name}</p>
                    {instructor.title && <p className="text-xs text-white/60">{instructor.title}</p>}
                  </div>
                </div>
              )}

              {/* live date + countdown */}
              {seminar && (
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-6 border border-white/10">
                  <p className="text-xs text-premium font-semibold uppercase tracking-wider mb-2">Nächster Termin</p>
                  <div className="flex items-center gap-2 text-sm mb-1">
                    <Calendar className="w-4 h-4 text-white/60" />
                    <span>{fmtDate(seminar.start_date)} – {fmtDate(seminar.end_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm mb-3">
                    <MapPin className="w-4 h-4 text-white/60" />
                    <span>{seminar.location}</span>
                  </div>

                  {/* countdown */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[
                      { val: countdown.days, lbl: "Tage" },
                      { val: countdown.hours, lbl: "Std." },
                      { val: countdown.mins, lbl: "Min." },
                    ].map((c) => (
                      <div key={c.lbl} className="bg-white/10 rounded p-2 text-center">
                        <div className="text-xl font-bold">{c.val}</div>
                        <div className="text-[9px] uppercase tracking-widest text-white/40">{c.lbl}</div>
                      </div>
                    ))}
                  </div>

                  {/* spots */}
                  {spotsLeft <= 5 ? (
                    <p className="text-premium text-sm font-semibold">⚡ Nur noch {spotsLeft} Plätze frei!</p>
                  ) : (
                    <p className="text-white/50 text-sm">{spotsLeft} von {seminar.max_participants} Plätzen verfügbar</p>
                  )}
                </div>
              )}

              {/* CTA */}
              <div className="mt-auto">
                <Link to={seminar ? `/seminare/${seminar.id}` : "/seminare"}>
                  <Button
                    size="lg"
                    className="w-full bg-gradient-to-r from-premium to-yellow-400 hover:from-yellow-500 hover:to-premium text-premium-foreground font-bold shadow-lg text-base"
                  >
                    Jetzt Platz in der Masterclass sichern <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default DualPathSelector;
